import { useMemo } from "react";

/**
 * Iter-101 — consistent Episode filter used by Cast, Notes, and other
 * episode-aware views. Renders a single-line horizontally-scrollable
 * pill bar:  [All] [1] [2] [Pilot] …
 *
 * Pass the project's episode list (from /projects/{id}/episodes) as
 * `episodes`. `value` is the currently-selected episode string, or
 * null for "All". `onChange(nextEpisodeOrNull)` is fired on tap.
 *
 * The component deliberately mirrors the visual language of the
 * scenes-view episode groups so the user sees "Episode 1" everywhere
 * it matters.
 */
export default function EpisodeFilter({
    episodes,
    value,
    onChange,
    testid = "episode-filter",
    label = "EP",
    className = "",
}) {
    const list = useMemo(() => {
        const seen = Array.isArray(episodes) ? episodes.filter(Boolean) : [];
        return seen.map(String);
    }, [episodes]);

    // Nothing to filter against — don't render the bar at all.
    if (list.length === 0) return null;

    const selected = value ? String(value) : null;

    const base =
        "tap shrink-0 h-8 px-3 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em] border transition-colors";
    const active =
        "bg-primary text-primary-foreground border-primary shadow-sm";
    const idle =
        "bg-transparent text-neutral-300 border-white/10 hover:border-white/25 hover:text-white";

    return (
        <div
            data-testid={testid}
            className={`flex items-center gap-2 mb-3 ${className}`}
        >
            <span className="shrink-0 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {label}
            </span>
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0 pb-0.5">
                <button
                    type="button"
                    data-testid={`${testid}-all`}
                    aria-pressed={selected === null}
                    onClick={() => onChange(null)}
                    className={`${base} ${selected === null ? active : idle}`}
                >
                    All
                </button>
                {list.map((ep) => (
                    <button
                        key={ep}
                        type="button"
                        data-testid={`${testid}-${ep}`}
                        aria-pressed={selected === ep}
                        onClick={() => onChange(ep)}
                        className={`${base} ${selected === ep ? active : idle}`}
                    >
                        EP {ep}
                    </button>
                ))}
            </div>
        </div>
    );
}
